import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Grid, Typography, CircularProgress } from '@mui/material';
import { CancelOutlined, SaveOutlined } from '@mui/icons-material';

import useEditAssociations from '../../../hooks/Locations/edit/useEditAssociations';
import useFetchAssociatonDetails from '../../../hooks/Locations/useFetchAssociatonDetails';
import usePatchAssociations from '../../../hooks/Locations/edit/usePatchAssociations';
import useUI from '../../../hooks/context/useUI';

import Styles from '../../Locations/Styles/ListCustodiansStyles';
import {
  cancelIcon,
  cancelIconText,
  iconContainer,
  saveIcon,
  saveIconText,
} from '../Styles/MainInformationTabStyles';
import { textPrimary } from '../../../assets/styles/appStyles';

import Associations from './Associations';
import { validateAssociationData } from '../../../utils/validationHelpers';

export default function EditAssociations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useUI();

  const { associationsRecords, isLoading } = useFetchAssociatonDetails(
    id ?? '',
  );

  const [associationData, setAssociationData] = useState<any>(null);
  const editMode = true;

  useEffect(() => {
    if (associationsRecords && !isLoading) {
      setAssociationData(associationsRecords);
    }
  }, [associationsRecords, isLoading]);

  const { associationsForm, providers, recordTypes } = useEditAssociations({
    editMode,
    associationData,
  });

  const { edit, isSaving } = usePatchAssociations(id);

  const cancelClickHandler = () => {
    navigate('/associations');
  };

  const saveClicked = async () => {
    associationsForm.submitForm();

    const errors = validateAssociationData(associationsForm.values);
    if (errors.length === 0) {
      const result = await edit(associationsForm.values);
      if (result) {
        console.log('Save successful');
      }
    } else {
      showSnackbar(errors, 'error');
    }
  };

  const renderAssociationsGrid = () => {
    if (isLoading) {
      return (
        <Typography sx={{ textAlign: 'center', mt: 2 }}>Loading...</Typography>
      );
    }

    if (!associationData || !associationsForm) {
      return (
        <Typography sx={{ textAlign: 'center', mt: 2 }}>
          No Record Found
        </Typography>
      );
    }

    return (
      <form name="associations" onSubmit={associationsForm.handleSubmit}>
        <Associations
          form={associationsForm}
          supportedRecordTypes={recordTypes}
          providers={providers}
          setAssociationData={setAssociationData}
          associationData={associationData}
          parentEntityId={associationData.parentEntityId}
        />
      </form>
    );
  };

  return (
    <>
      <Grid container direction="column" p={1}>
        <Grid item>
          <Typography sx={Styles.headerTitleStyles}>
            Associations Management
          </Typography>
        </Grid>

        <Grid item mt={3}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, color: '#f5831f' }}>
            {associationData?.parentEntityName ?? 'Loading...'}
          </Typography>
        </Grid>

        <Grid
          item
          container
          justifyContent="space-between"
          alignItems="center"
          mt={1}
        >
          <Grid item>
            <Typography sx={textPrimary}>
              Total number of associations (
              {associationData?.entityAssociations?.length ?? 0})
            </Typography>
          </Grid>

          {editMode && (
            <Grid item>
              <Grid container spacing={1}>
                <Grid item>
                  {isSaving ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Box
                      data-testid="cancel"
                      sx={iconContainer}
                      onClick={cancelClickHandler}
                    >
                      <CancelOutlined sx={cancelIcon} />
                      <Typography sx={cancelIconText}>Cancel</Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item>
                  {isSaving ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Box
                      data-testid="save"
                      sx={iconContainer}
                      onClick={saveClicked}
                    >
                      <SaveOutlined sx={saveIcon} />
                      <Typography sx={saveIconText}>Save</Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid container my={2}>
        <Grid item xs={12}>
          {renderAssociationsGrid()}
        </Grid>
      </Grid>
    </>
  );
}
